import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { PortalService } from '../portal.service';
import { PortalMetrics } from '../../shared/configurations/portal-homepage.constant';
import { BehaviorSubject, combineLatest, Subject, filter, takeUntil } from 'rxjs';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { PortalMetricsComponent } from '../portal-metrics/portal-metrics.component';
import { DownloadMapDatatableComponent } from './download-map-datatable/download-map-datatable.component';
import { DeferLoadModule } from '@trademe/ng-defer-load';
import { WorldMapComponent } from '@exl-ng/mulo-common';


export class Extra {
  code?: string;
  color?: string;

  constructor(code) {
    this.code = code;
  }
}

export class CountryDownloads {
  name?: string;
  value: number;
  extra: Extra;

  constructor(name, value) {
    this.name = name;
    this.value = value;
  }
}

@Component({
    selector: 'esp-portal-download-map',
    templateUrl: './portal-download-map.component.html',
    styleUrls: ['./portal-download-map.component.scss'],
    imports: [
        WorldMapComponent,
        DeferLoadModule,
        DownloadMapDatatableComponent,
        PortalMetricsComponent,
        TranslateModule
    ]
})
export class PortalDownloadMapComponent implements OnInit, OnDestroy {
  private portalService = inject(PortalService);
  private translate = inject(TranslateService);

  showMetricsDownloads = true;
  loading = false;
  mapReady = false;
  countryInFocus: string;

  downloadsByCountry: CountryDownloads[] = [];
  countriesColors = [];

  isSvnReady: BehaviorSubject<boolean> = new BehaviorSubject(false);
  componentDestroy = new Subject<void>();

  ngOnInit() {
    const svnReady$ = this.isSvnReady.pipe(filter((isReady) => isReady));
    const mayByCountry$ = this.portalService.getDownloadsMapByCountry();
    combineLatest([svnReady$, mayByCountry$])
      .pipe(takeUntil(this.componentDestroy))
      .subscribe(([isReady, data]) => {
        if (data) {
          this.buildDownloadsMap(data);
        } else {
          this.mapReady = true;
        }
      });
  }

  buildDownloadsMap(data) {
    this.downloadsByCountry = data as CountryDownloads[];
    this.loading = false;
    // this.downloadsByCountry = [
    //     {name: "Afghanistan", value: 200, extra: {code: "af", color:''}},
    //     {name: "Brazil", value: 48, extra: {code: "br", color:''}},
    //     {name: null, value: 50, extra: {code: null, color:''}},
    //     {value: 30, extra: {}},
    //     {name: "Japan", value: 32, extra: {code: "jp", color:''}},
    //     // {name: "Bla1", value: 9, extra: {code: "be", color:''}},
    //     {name: "Sweden", value: 15, extra: {code: "se", color:''}},
    //     {name: "Turkey", value: 13, extra: {code: "tr", color:''}},
    //     {name: "Bla22", value: 1, extra: {code: "eng", color:''}}];
    this.downloadsByCountry = this.downloadsByCountry.filter(
      (country) => country.value > 0
    );

    const unknown = this.getUsageForUnknownCountries();

    this.downloadsByCountry = this.downloadsByCountry.filter(
      (country) => country.name
    );
    this.downloadsByCountry.sort((a, b) => {
      return b.value - a.value;
    });

    this.groupDownloadsUsage(unknown);
    this.prepareColors();
    this.translateAllOthersKey();
  }

  translateAllOthersKey() {
    if (
      this.downloadsByCountry?.length > 0 &&
      this.downloadsByCountry[this.downloadsByCountry.length - 1].name ===
      PortalMetrics.OTHERS
    ) {
      this.translate.get(PortalMetrics.OTHERS).subscribe(
        (data) => {
          this.downloadsByCountry[
            this.downloadsByCountry.length - 1
          ].name = data;
          this.mapReady = true;
        },
        () => {
          this.mapReady = true;
        }
      );
    } else {
      this.mapReady = true;
    }
  }

  groupDownloadsUsage(unknown) {
    if (this.downloadsByCountry.length > 6) {
      const temp = this.downloadsByCountry.slice(0, 5);
      temp[5] = new CountryDownloads(PortalMetrics.OTHERS, 0);
      for (let i = 5; i < this.downloadsByCountry.length; i++) {
        temp[5].value =
          temp[5].value + this.downloadsByCountry[i].value;
      }
      if (unknown) {
        temp[5].value = temp[5].value + unknown;
      }
      this.downloadsByCountry = temp;
    } else if (this.downloadsByCountry.length < 6) {
      if (unknown > 0) {
        const other = new CountryDownloads(
          PortalMetrics.OTHERS,
          unknown
        );
        this.downloadsByCountry.push(other);
      }
    } else if (this.downloadsByCountry.length === 6) {
      if (unknown) {
        const other = new CountryDownloads(
          PortalMetrics.OTHERS,
          this.downloadsByCountry[5].value + unknown
        );
        this.downloadsByCountry[5] = other;
      }
    }
  }

  getUsageForUnknownCountries() {
    const reducer = (value1, value2) => value1 + value2;
    const unknown = this.downloadsByCountry
      .filter((country) => !country.name)
      .map((country) => {
        return country.value;
      });
    if (unknown.length > 0) {
      return unknown.reduce(reducer);
    }
    return 0;
  }

  onMetricFocus(event) {
    this.countryInFocus =
      event && event.value && event.value.extra
        ? event.value.extra.code
        : null;
  }

  prepareColors() {
    const colorByCountryCodeArray = [];
    for (let i = 0; i < this.downloadsByCountry.length; i++) {
      const country = this.downloadsByCountry[i];
      if (country.name !== PortalMetrics.OTHERS) {
        const extra = new Extra(country.extra.code);
        extra.color = PortalMetrics.COLORS_SCHEMA[i];
        colorByCountryCodeArray.push(extra);
      }
    }
    this.countriesColors = colorByCountryCodeArray;
  }

  ngOnDestroy() {
    if (!this.componentDestroy.isStopped) {
      this.componentDestroy.next();
      this.componentDestroy.unsubscribe();
    }
  }
}
